import { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaHeart, FaRegHeart } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { getProductImage, handleImageError } from '../utils/imageHelper';
import { generateProductAltText, productPath } from '../utils/seo';

const NEW_FOR_DAYS = 30;
const BESTSELLER_MIN_SOLD = 25;

// One merchandising tag per card, strongest signal first — all from real data
const merchTag = (p) => {
  if (p.sold >= BESTSELLER_MIN_SOLD) return { label: 'Bestseller', cls: 'bg-gold-400 text-espresso' };
  if (p.averageRating >= 4.5 && p.numReviews >= 3) return { label: 'Top rated', cls: 'bg-sage-500 text-white' };
  if (p.createdAt && Date.now() - new Date(p.createdAt) < NEW_FOR_DAYS * 864e5) return { label: 'New', cls: 'bg-espresso text-white' };
  if (p.isFeatured) return { label: 'Staff pick', cls: 'bg-white/95 text-espresso shadow-soft' };
  return null;
};

const ProductCard = ({ product }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const [adding, setAdding] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const { addToCart } = useCart();

  const hasMulti = product.images?.length > 1;
  const inStock = product.stock > 0;
  const price = product.discountPrice || product.price;
  const discount = product.discountPrice
    ? Math.round(((product.price - product.discountPrice) / product.price) * 100) : 0;
  const ageGroup = product.ageGroup && product.ageGroup !== 'All Ages' ? product.ageGroup : null;
  const tag = merchTag(product);
  const href = productPath(product);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    try {
      await addToCart(product._id, 1, '', product.colors?.[0]?.name || 'Default');
    } catch (err) { console.error(err); }
    finally { setAdding(false); }
  };

  return (
    <article className="group relative flex flex-col h-full bg-white rounded-2xl border border-skin-100 overflow-hidden transition duration-300 hover:shadow-medium hover:border-skin-200">
      {/* Image */}
      <div className="relative aspect-square bg-skin-50 overflow-hidden"
        onMouseEnter={() => hasMulti && setImgIdx(1)}
        onMouseLeave={() => setImgIdx(0)}>
        <Link to={href} className="block w-full h-full" tabIndex={-1} aria-hidden="true">
          <img
            src={getProductImage(product, imgIdx)}
            alt={generateProductAltText(product, imgIdx)}
            onError={e => handleImageError(e, 'product')}
            width="600" height="600"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        </Link>

        <div className="absolute top-2.5 left-2.5 flex flex-col items-start gap-1.5 z-10">
          {tag && (
            <span className={`px-2 py-1 rounded-md font-sans font-bold text-[10px] leading-none uppercase tracking-wide ${tag.cls}`}>
              {tag.label}
            </span>
          )}
          {discount > 0 && (
            <span className="px-2 py-1 rounded-md bg-terra-500 text-white font-sans font-bold text-[11px] leading-none">
              -{discount}%
            </span>
          )}
          {inStock && product.stock < 10 && (
            <span className="px-2 py-1 rounded-md bg-white/95 text-espresso font-sans font-semibold text-[10px] leading-none shadow-soft">
              Only {product.stock} left
            </span>
          )}
        </div>

        <button
          type="button"
          className="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full bg-white/95 shadow-soft flex items-center justify-center text-espresso transition hover:text-terra"
          onClick={e => { e.preventDefault(); e.stopPropagation(); setWishlisted(w => !w); }}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          aria-pressed={wishlisted}>
          {wishlisted ? <FaHeart className="text-xs text-terra" /> : <FaRegHeart className="text-xs" />}
        </button>

        {ageGroup && (
          <span className="absolute bottom-2.5 left-2.5 z-10 px-2 py-1 rounded-full bg-white/95 shadow-soft font-sans font-semibold text-[10px] leading-none text-sage-600">
            {ageGroup}
          </span>
        )}

        {!inStock && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
            <span className="px-3 py-1.5 rounded-full bg-espresso text-white font-sans font-semibold text-xs">Out of stock</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex flex-col flex-1 p-3 md:p-3.5">
        <h3 className="font-sans font-semibold text-[13px] md:text-sm leading-snug text-espresso line-clamp-2 min-h-[2.5em]">
          <Link to={href} className="hover:text-terra transition-colors">
            {product.name}
          </Link>
        </h3>

        {product.averageRating > 0 && product.numReviews > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            <FaStar className="text-[11px] text-gold-400" />
            <span className="font-sans text-[11px] font-semibold text-espresso">{product.averageRating.toFixed(1)}</span>
            <span className="font-sans text-[11px] text-wood-400">({product.numReviews} {product.numReviews === 1 ? 'review' : 'reviews'})</span>
          </div>
        )}

        <div className="mt-auto pt-2.5 flex items-baseline flex-wrap gap-x-2">
          <span className="font-sans font-bold text-base md:text-lg text-espresso">₹{price.toLocaleString('en-IN')}</span>
          {discount > 0 && (
            <span className="font-sans text-xs text-wood-400 line-through">₹{product.price.toLocaleString('en-IN')}</span>
          )}
        </div>

        {inStock && (
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={adding}
            className="mt-3 w-full py-2.5 rounded-full border border-espresso font-sans font-semibold text-xs text-espresso transition hover:bg-espresso hover:text-white disabled:opacity-60">
            {adding ? 'Adding…' : 'Add to cart'}
          </button>
        )}
      </div>
    </article>
  );
};

// Lists re-render as each home section loads; unchanged cards skip the work
export default memo(ProductCard);
